import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close } from "@mui/icons-material";
import { Avatar, Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useRef } from "react";
import { useNotifications } from "../hooks";
const DEFAULT_ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp'
];
const DEFAULT_ACCEPT_ATTR = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';
const DEFAULT_MAX_SIZE_BYTES = 1 * 1024 * 1024;
const DEFAULT_MAX_SIZE_LABEL = '1MB';
const fileToBase64 = (file)=>new Promise((resolve, reject)=>{
        const reader = new FileReader();
        reader.onerror = ()=>reject(reader.error ?? new Error('Read failed'));
        reader.onload = ()=>{
            const result = reader.result;
            if (typeof result !== 'string') {
                reject(new Error('Unexpected reader result'));
                return;
            }
            resolve(result.replace(/^data:[^;]+;base64,/, ''));
        };
        reader.readAsDataURL(file);
    });
export const ImageUploader = (props)=>{
    const { accept = DEFAULT_ACCEPT, acceptAttr = DEFAULT_ACCEPT_ATTR, alt = 'Image preview', clearable = false, disabled = false, emptyPlaceholder = 'No image uploaded', helperText, imagePreviewSx, innerImageSx, invalidTypeMessage = 'Unsupported image type.', isUploading = false, label = 'Upload image', maxSizeBytes = DEFAULT_MAX_SIZE_BYTES, maxSizeLabel = DEFAULT_MAX_SIZE_LABEL, onChange, onUpload, oversizedMessage = `Image must be ${maxSizeLabel} or smaller.`, previewShape = 'rounded', previewSize = 96, previewSrc, previewVariant = 'avatar', registerCtx, uploadingLabel = 'Uploading...', value } = props;
    const acceptCaption = props.acceptCaption ?? `PNG, JPG, or WEBP. Max file size ${maxSizeLabel}.`;
    const notifications = useNotifications();
    const fileInputRef = useRef(null);
    const testId = props['data-testid'];
    const onPickFile = ()=>{
        fileInputRef.current?.click();
    };
    const onFileChange = async (event)=>{
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!accept.includes(file.type)) {
            notifications.error(invalidTypeMessage);
            return;
        }
        if (file.size > maxSizeBytes) {
            notifications.error(oversizedMessage);
            return;
        }
        try {
            const content = await fileToBase64(file);
            const result = await onUpload({
                content,
                contentType: file.type
            });
            onChange(result.key);
        } catch  {
        /* upstream onError handler should surface upload failures */ }
    };
    const onClear = ()=>{
        onChange('');
    };
    const buttonLabel = isUploading ? uploadingLabel : label;
    const buttonDisabled = disabled || isUploading;
    const hasValue = !!value;
    const preview = previewVariant === 'image' ? /*#__PURE__*/ _jsx(Box, {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        sx: [
            {
                backgroundColor: 'action.hover',
                borderRadius: 1,
                minHeight: 96,
                px: 2,
                py: 1,
                width: '100%'
            },
            ...Array.isArray(imagePreviewSx) ? imagePreviewSx : [
                imagePreviewSx
            ]
        ],
        children: previewSrc ? /*#__PURE__*/ _jsx(Box, {
            alt: alt,
            component: "img",
            "data-testid": testId ? `${testId}Preview` : undefined,
            src: previewSrc,
            sx: [
                {
                    maxHeight: 250,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    width: 'auto'
                },
                ...Array.isArray(innerImageSx) ? innerImageSx : [
                    innerImageSx
                ]
            ]
        }) : /*#__PURE__*/ _jsx(Typography, {
            color: "text.secondary",
            variant: "caption",
            children: emptyPlaceholder
        })
    }) : /*#__PURE__*/ _jsx(Avatar, {
        alt: alt,
        "data-testid": testId ? `${testId}Preview` : undefined,
        src: previewSrc,
        sx: {
            height: previewSize,
            width: previewSize
        },
        variant: previewShape
    });
    return /*#__PURE__*/ _jsxs(Stack, {
        alignItems: "center",
        spacing: 1,
        children: [
            preview,
            /*#__PURE__*/ _jsxs(Stack, {
                alignItems: "center",
                direction: "row",
                spacing: 0.5,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        "data-testid": testId,
                        disabled: buttonDisabled,
                        onClick: onPickFile,
                        size: "small",
                        variant: "outlined",
                        children: buttonLabel
                    }),
                    clearable && hasValue && /*#__PURE__*/ _jsx(IconButton, {
                        "aria-label": "Remove image",
                        "data-testid": testId ? `${testId}Clear` : undefined,
                        disabled: buttonDisabled,
                        onClick: onClear,
                        size: "small",
                        children: /*#__PURE__*/ _jsx(Close, {
                            fontSize: "small"
                        })
                    })
                ]
            }),
            (helperText ?? acceptCaption) && /*#__PURE__*/ _jsx(Typography, {
                color: "text.secondary",
                variant: "caption",
                children: helperText ?? acceptCaption
            }),
            /*#__PURE__*/ _jsx(Box, {
                accept: acceptAttr,
                component: "input",
                "data-testid": testId ? `${testId}FileInput` : undefined,
                onChange: onFileChange,
                ref: fileInputRef,
                sx: {
                    display: 'none'
                },
                type: "file"
            }),
            registerCtx && /*#__PURE__*/ _jsx("input", {
                type: "hidden",
                ...registerCtx
            })
        ]
    });
};
ImageUploader.displayName = 'ImageUploader';

//# sourceMappingURL=ImageUploader.js.map